import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { VatTreatmentFields, vatPayload, vatError } from '@/components/VatTreatmentFields';
import { DashboardLayout } from '@/components/DashboardLayout';
import PageTour from '@/components/PageTour';
import BranchLink from '@/components/BranchLink';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Trash2, DollarSign, FileText, ArrowUpCircle, ArrowDownCircle, Receipt, CreditCard, Banknote, Download, Building2, X, Send, Mail, MessageSquare, Loader2, Truck, Undo2, LayoutGrid, List, Sparkles, AlertTriangle, ScanLine, Link2, Link2Off, ChevronDown, ShieldCheck, MoreHorizontal } from 'lucide-react';
import { RiyalIcon, getCurrencySymbol } from '@/lib/currency';
import { useTaxConfig } from '@/hooks/useTaxConfig';
import api from '@/lib/api';
import { invalidateSupplierReconHealthCache } from '@/components/dashboard/SupplierReconciliationHealthWidget';
import BankPaymentDialog from '@/components/BankPaymentDialog';
import { SupplierReconcileDialog } from '@/components/suppliers/SupplierReconcileDialog';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { ExportButtons } from '@/components/ExportButtons';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { AdvancedSearch, applySearchFilters } from '@/components/AdvancedSearch';
import { useBranchStore } from '@/stores';
import { useAuthStore } from '@/stores/authStore';
import { PageInfoCallout } from '@/components/common/PageInfoCallout';
import { PlatformIbanBanner } from '@/components/banking/PlatformIbanBanner';
import { MoversPanel } from '@/components/common/MoversPanel';
import { SuppliersTour } from '@/components/suppliers/SuppliersTour';
import { localToday } from '@/lib/dateRange';
import { useRealtimeRefresh } from '@/hooks/useRealtimeRefresh';

// Check if the current user can write supplier-payment records. Branch
// users and employees often have only `expenses:write` — the backend's
// expense handler already calls `recalculate_supplier_credit`, so the
// secondary supplier-payments POST from the purchase-bill flow is a
// history-only convenience. Gating it here avoids the 403 log spam that
// was showing up in production deploy logs.
function _canWriteSupplierPayment(user) {
  if (!user) return false;
  if (user.role === 'admin' || user.is_super_admin) return true;
  const perms = user.permissions || {};
  if (Array.isArray(perms)) return perms.includes('supplier_payments');
  return perms.supplier_payments === 'write';
}

export default function SuppliersPage() {
  const { user } = useAuthStore();
  // iter617 — Use tenant currency for all credit/balance displays.
  const { taxConfig } = useTaxConfig();
  const CUR = getCurrencySymbol(taxConfig?.currency || 'SAR');
  const canWriteSupplierPayment = _canWriteSupplierPayment(user);
  const isAdmin = user?.role === 'admin' || user?.is_super_admin;
  const [suppliers, setSuppliers] = useState([]);
  // Cash Flow Movers — same UX language as Banking Top Movers / Sales
  // Movers. Hidden when there's no movement to show.
  const [cashflowMovers, setCashflowMovers] = useState([]);
  const [showSuppliersTour, setShowSuppliersTour] = useState(false);
  const { branches, fetchBranches } = useBranchStore();
  const [categories, setCategories] = useState([]);
  const [searchFilters, setSearchFilters] = useState({});
  const [paySummaries, setPaySummaries] = useState({});
  // iter442 — view-mode toggle (grid cards ↔ compact list rows), persisted per user
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem('suppliers.viewMode') || 'grid'; } catch { return 'grid'; }
  });
  useEffect(() => {
    try { localStorage.setItem('suppliers.viewMode', viewMode); } catch { /* ignore */ }
  }, [viewMode]);
  const [loading, setLoading] = useState(true);
  // iter724 — Audit banner: count of unmatched outgoing bank txns
  // that may include supplier payments which silently failed under
  // the pre-fix bug (frontend never sent paying_bank_account_id,
  // backend HTTP-400'd into skipped[], toast lied). Surface the
  // count here so the user can jump straight to Recon Cockpit and
  // re-pair the historical losses.
  const [lostPaymentsScan, setLostPaymentsScan] = useState(null);
  const [showDialog, setShowDialog] = useState(false);
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [reconcileSupplier, setReconcileSupplier] = useState(null); // iter570
  // "Pay from Bank" shortcut → reuses the unified BankPaymentDialog so
  // the user doesn't have to navigate to /bank-payments separately.
  const [showBankPay, setShowBankPay] = useState(false);
  const [bankPaySeed, setBankPaySeed] = useState(null);
  const [showLedgerDialog, setShowLedgerDialog] = useState(false);
  const [ledgerFilter, setLedgerFilter] = useState('all'); // all | purchases | payments | reversed
  const [showAddBillDialog, setShowAddBillDialog] = useState(false);
  const [billData, setBillData] = useState({ amount: '', category: '', payment_mode: 'credit', description: '', branch_id: '' });
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);
  const [ledgerStartDate, setLedgerStartDate] = useState('');
  const [ledgerEndDate, setLedgerEndDate] = useState('');
  // iter526 — branch filter on Supplier Ledger (was requested earlier but missed)
  const [ledgerBranchId, setLedgerBranchId] = useState('all');
  const [ledgerOpening, setLedgerOpening] = useState(true); // H2.1 — include opening balance (account-statement mode)
  const [ledgerSupplier, setLedgerSupplier] = useState(null);
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [payingSupplier, setPayingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '', category: '', sub_category: '', branch_id: '', phone: '', email: '', account_number: '', credit_limit: 0, vat_number: '',
    bank_accounts: []
  });
  const [paymentData, setPaymentData] = useState({ payment_mode: 'cash', amount: '', branch_id: '' });
  const [newCategory, setNewCategory] = useState('');
  const [newSubCategory, setNewSubCategory] = useState('');
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [shareData, setShareData] = useState({ channels: [], email: '', phone: '' });
  const [sharing, setSharing] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      // Use Zustand for branches
      fetchBranches();
      const [suppliersRes, categoriesRes, summariesRes, expCatRes] = await Promise.all([
        api.get('/suppliers'),
        api.get('/categories?category_type=supplier'),
        api.get('/suppliers/payment-summaries'),
        api.get('/categories?category_type=expense').catch(() => ({ data: [] })),
      ]);
      setSuppliers(suppliersRes.data);
      setCategories(categoriesRes.data);
      setPaySummaries(summariesRes.data);
      setExpenseCategories(expCatRes.data || []);
      // iter580 — Any visit to the Suppliers page is implicitly an opportunity
      // for the user to make a mutation, AND the data we just fetched here is
      // the same data the dashboard widget consumes. Invalidate the widget's
      // 5-min cache so revisiting the dashboard reflects any change made
      // here without waiting for the TTL.
      invalidateSupplierReconHealthCache();
      // Cash flow movers — silently degrade (older backend / no payments).
      api.get('/suppliers/cashflow-movers?window_days=7&limit=3')
        .then((r) => setCashflowMovers(r.data?.movers || []))
        .catch(() => setCashflowMovers([]));
      // iter724 — Lost-payment audit. Light call to recon-cockpit unmatched
      // endpoint, filtered to OUTGOING (debit) only. We only need totals,
      // not the full row list, so we cap the limit and inspect length.
      api.get('/recon-cockpit/unmatched', { params: { kind: 'debit', limit: 200, banner: 1, from_date: new Date(Date.now() - 60 * 864e5).toISOString().slice(0, 10) } })
        .then((r) => {
          const rows = r.data?.rows || [];
          const total = Number(r.data?.total || rows.length);
          const total_amount = rows.reduce((s, x) => s + Number(x.amount || 0), 0);
          // A row is "likely supplier payment" if its top candidate is
          // a supplier_payment OR the description has letters (real
          // merchant names, not bank-internal codes like VISA TRM).
          const likelySupplier = rows.filter((x) => {
            const c = x.best || {};
            const isSup = (c.type === 'supplier_payment') || (c.payee_type === 'supplier');
            const desc = String(x.description || '');
            const looksLikeMerchant = /[A-Za-z\u0600-\u06FF]{3,}/.test(desc) && !/^\s*(TXN|REF|VISA|MADA|SPAN|POS|ATM)\b/i.test(desc);
            return isSup || looksLikeMerchant;
          });
          setLostPaymentsScan({
            total,
            total_amount,
            likely_supplier: likelySupplier.length,
            likely_amount: likelySupplier.reduce((s, x) => s + Number(x.amount || 0), 0),
          });
        })
        .catch(() => setLostPaymentsScan(null));
    } catch { toast.error('Failed to fetch data'); }
    finally { setLoading(false); }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await api.post('/categories', { name: newCategory.trim(), type: 'supplier' });
      toast.success('Category added');
      setNewCategory('');
      const res = await api.get('/categories?category_type=supplier');
      setCategories(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add category'); }
  };

  const handleAddSubCategory = async () => {
    if (!newSubCategory.trim() || !formData.category) return;
    try {
      const parent = categories.find(c => c.name === formData.category);
      await api.post('/categories', { name: newSubCategory.trim(), type: 'supplier', parent_id: parent?.id || null });
      toast.success('Sub-category added');
      setNewSubCategory('');
      const res = await api.get('/categories?category_type=supplier');
      setCategories(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed'); }
  };

  const subCategories = categories.filter(c => {
    const parent = categories.find(p => p.name === formData.category && !p.parent_id);
    return c.parent_id && parent && c.parent_id === parent.id;
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = { ...formData };
      // Validate bank accounts (max 3, filter empty)
      submitData.bank_accounts = (formData.bank_accounts || [])
        .filter(ba => ba.bank_name || ba.account_number || ba.iban)
        .slice(0, 3);
      if (editingSupplier) {
        await api.put(`/suppliers/${editingSupplier.id}`, submitData);
        toast.success('Supplier updated successfully');
      } else {
        await api.post('/suppliers', submitData);
        toast.success('Supplier added successfully');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save supplier'); }
  };

  const handlePayCredit = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/suppliers/${payingSupplier.id}/pay-credit`, paymentData);
      toast.success('Credit payment recorded');
      setShowPayDialog(false);
      setPaymentData({ payment_mode: 'cash', amount: '', branch_id: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to record payment'); }
  };

  const viewLedger = async (supplier, startDate, endDate, branchId) => {
    setLedgerLoading(true);
    setLedgerSupplier(supplier);
    setShowLedgerDialog(true);
    try {
      let url = `/suppliers/${supplier.id}/ledger`;
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      // iter526 — branch filter (server already supports ?branch_id=)
      if (branchId && branchId !== 'all') params.push(`branch_id=${branchId}`);
      params.push(`include_opening=${ledgerOpening}`);
      if (params.length > 0) url += `?${params.join('&')}`;
      const res = await api.get(url);
      setLedgerData(res.data);
    } catch { toast.error('Failed to load supplier ledger'); setShowLedgerDialog(false); }
    finally { setLedgerLoading(false); }
  };

  const reversePayment = async (entry) => {
    if (!entry?.payment_id) { toast.error('Not reversible'); return; }
    const reason = window.prompt(`Reverse this payment of ${CUR} ${(entry.original_amount || entry.credit || 0).toFixed(2)}?\nOptional reason (kept as audit note):`);
    if (reason === null) return; // user cancelled
    try {
      await api.post(`/supplier-payments/${entry.payment_id}/reverse`, { reason });
      toast.success('Payment reversed');
      // Reload ledger + list in the background
      if (ledgerSupplier) await viewLedger(ledgerSupplier, ledgerStartDate, ledgerEndDate, ledgerBranchId);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reverse');
    }
  };

  // H3 — realtime refresh: when any supplier-affecting transaction changes
  // (purchase/credit purchase via expense events, supplier payment, credit
  // return, bill return, supplier update), refetch the list/cards AND, if a
  // ledger is open, re-run it with its EXACT current supplier/branch/date/
  // Opening ON-OFF. All figures come from the API — no frontend recompute.
  useRealtimeRefresh(
    ['supplier_changed', 'new_expense', 'expense_updated', 'expense_deleted'],
    () => {
      fetchData();
      if (ledgerSupplier) viewLedger(ledgerSupplier, ledgerStartDate, ledgerEndDate, ledgerBranchId);
    },
  );

  const exportLedger = async (format) => {
    if (!ledgerSupplier) return;
    try {
      let url = `/suppliers/${ledgerSupplier.id}/ledger/export?format=${format}`;
      if (ledgerStartDate) url += `&start_date=${ledgerStartDate}`;
      if (ledgerEndDate) url += `&end_date=${ledgerEndDate}`;
      // iter526 — propagate branch filter to the export so the PDF/Excel matches what's on screen
      if (ledgerBranchId && ledgerBranchId !== 'all') url += `&branch_id=${ledgerBranchId}`;
      url += `&include_opening=${ledgerOpening}`;
      const res = await api.get(url, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `ledger_${ledgerSupplier.name.replace(/\s+/g, '_')}.${format === 'excel' ? 'xlsx' : format === 'csv' ? 'csv' : 'pdf'}`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success(`Ledger exported as ${format.toUpperCase()}`);
    } catch { toast.error('Failed to export ledger'); }
  };

  const handleAddBill = async (e) => {
    e.preventDefault();
    if (!payingSupplier) return;
    const _ve = vatError(billData);
    if (_ve) { toast.error(_ve); return; }
    try {
      await api.post('/expenses', {
        amount: parseFloat(billData.amount),
        category: billData.category || 'Supplier Purchase',
        description: billData.description || `Purchase from ${payingSupplier.name}`,
        payment_mode: billData.payment_mode,
        supplier_id: payingSupplier.id,
        branch_id: billData.branch_id || '',
        date: `${localToday()}T${new Date().toTimeString().slice(0,8)}`,
        ...vatPayload(billData),
      });

      // iter395f — Removed dual-write to /supplier-payments. Previously
      // we POSTed a `supplier_payments` row in addition to the expense,
      // which polluted the "Settlements of old credit bills" report
      // because cash/bank purchases were being mis-classified as
      // credit-bill settlements. The expense alone fully captures the
      // cash outflow and updates supplier credit via
      // recalculate_supplier_credit (called inside POST /expenses).
      // supplier_payments should only be created via the explicit
      // Pay Credit dialog when actually settling a credit bill.

      const modeText = billData.payment_mode === 'credit' ? 'Credit bill added to balance' : 'Cash/Bank bill recorded';
      toast.success(`Purchase bill recorded! ${modeText}`);
      setShowAddBillDialog(false);
      setBillData({ amount: '', category: '', payment_mode: 'credit', description: '', branch_id: '' });
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add purchase bill'); }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      category: supplier.category || '',
      sub_category: supplier.sub_category || '',
      branch_id: supplier.branch_id || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      account_number: supplier.account_number || '',
      credit_limit: supplier.credit_limit || 0,
      vat_number: supplier.vat_number || '',
      bank_accounts: supplier.bank_accounts || []
    });
    setShowDialog(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await api.delete(`/suppliers/${id}`);
        toast.success('Supplier deleted successfully');
        fetchData();
      } catch { toast.error('Failed to delete supplier'); }
    }
  };

  const resetForm = () => {
    setFormData({ name: '', category: '', sub_category: '', branch_id: '', phone: '', email: '', account_number: '', credit_limit: 0, vat_number: '', bank_accounts: [] });
    setEditingSupplier(null);
  };

  // Bank account helpers
  const addBankAccount = () => {
    if ((formData.bank_accounts || []).length >= 3) {
      toast.error('Maximum 3 bank accounts allowed');
      return;
    }
    setFormData({
      ...formData,
      bank_accounts: [...(formData.bank_accounts || []), { bank_name: '', account_number: '', iban: '', swift_code: '' }]
    });
  };

  const updateBankAccount = (index, field, value) => {
    const updated = [...(formData.bank_accounts || [])];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, bank_accounts: updated });
  };

  const removeBankAccount = (index) => {
    setFormData({ ...formData, bank_accounts: formData.bank_accounts.filter((_, i) => i !== index) });
  };

  // Share statement
  const openShareDialog = () => {
    setShareData({
      channels: [],
      email: ledgerData?.supplier?.email || ledgerSupplier?.email || '',
      phone: ledgerData?.supplier?.phone || ledgerSupplier?.phone || ''
    });
    setShowShareDialog(true);
  };

  const toggleShareChannel = (channel) => {
    setShareData(prev => ({
      ...prev,
      channels: prev.channels.includes(channel)
        ? prev.channels.filter(c => c !== channel)
        : [...prev.channels, channel]
    }));
  };

  const shareStatement = async () => {
    if (shareData.channels.length === 0) { toast.error('Select at least one channel'); return; }
    if (shareData.channels.includes('email') && !shareData.email) { toast.error('Enter email address'); return; }
    if (shareData.channels.includes('whatsapp') && !shareData.phone) { toast.error('Enter phone number'); return; }
    setSharing(true);
    try {
      const res = await api.post(`/suppliers/${ledgerSupplier.id}/share-statement`, {
        channels: shareData.channels,
        email: shareData.email,
        phone: shareData.phone,
        start_date: ledgerStartDate || undefined,
        end_date: ledgerEndDate || undefined
      });
      const r = res.data.results;
      if (r.email?.success) toast.success(`Statement sent to ${r.email.sent_to}`);
      if (r.email && !r.email.success) toast.error(`Email: ${r.email.error}`);
      if (r.whatsapp?.success) toast.success(`WhatsApp sent to ${r.whatsapp.sent_to}`);
      if (r.whatsapp && !r.whatsapp.success) toast.error(`WhatsApp: ${r.whatsapp.error}`);
      setShowShareDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to share statement');
    } finally { setSharing(false); }
  };

  // Migration
  const [showMigrateDialog, setShowMigrateDialog] = useState(false);
  const [migrationPreview, setMigrationPreview] = useState(null);
  const [migrating, setMigrating] = useState(false);

  const previewMigration = async () => {
    try {
      const res = await api.get('/suppliers/migration-preview');
      setMigrationPreview(res.data);
      setShowMigrateDialog(true);
    } catch { toast.error('Failed to preview migration'); }
  };

  const executeMigration = async () => {
    setMigrating(true);
    try {
      const res = await api.post('/suppliers/migrate-payments-to-bills');
      toast.success(res.data.message);
      setShowMigrateDialog(false);
      setMigrationPreview(null);
      fetchData();
    } catch (error) { toast.error(error.response?.data?.detail || 'Migration failed'); }
    finally { setMigrating(false); }
  };

  // iter524 — Cleanup auto-created suppliers spawned from bank-statement
  // transaction codes (VISA TRM …-TXN:…-C0000, "Unnamed supplier" etc).
  // Two-step UX: dry-run first to preview, then confirm to delete.
  const [cleaningJunk, setCleaningJunk] = useState(false);
  const cleanupAutoCreated = async () => {
    setCleaningJunk(true);
    try {
      const preview = await api.post('/suppliers/cleanup-auto-created', { dry_run: true });
      const { candidates = 0, kept_with_data = 0, samples = [] } = preview.data || {};
      const deletable = (samples || []).filter(s => s.action === 'would_delete').length;
      if (candidates === 0) {
        toast.success('No auto-created suppliers found — your list is already clean.');
        return;
      }
      const previewNames = (samples || []).slice(0, 5).map(s => `• ${s.name}`).join('\n');
      const ok = window.confirm(
        `Found ${candidates} auto-created suppliers from bank statements.\n` +
        `${deletable} can be safely deleted (no bills/payments).\n` +
        `${kept_with_data} will be kept because they have linked bills or payments.\n\n` +
        `Examples:\n${previewNames}\n\nDelete the ${deletable} junk suppliers?`
      );
      if (!ok) return;
      const res = await api.post('/suppliers/cleanup-auto-created', {});
      const { deleted = 0, kept_with_data: kept = 0 } = res.data || {};
      toast.success(`Deleted ${deleted} auto-created suppliers. Kept ${kept} that have data.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Cleanup failed');
    } finally {
      setCleaningJunk(false);
    }
  };

  // iter724 — Backfill empty / placeholder supplier names from their
  // earliest expense's description. Triggered by user feedback after
  // iter723's Top Suppliers list showed many "Unknown" rows. Two-step
  // UX: dry-run preview first, then confirm to apply.
  const [backfilling, setBackfilling] = useState(false);
  const backfillNames = async () => {
    setBackfilling(true);
    try {
      const preview = await api.post('/suppliers/backfill-names', { dry_run: true });
      const { renamed = 0, samples = [] } = preview.data || {};
      const wouldRename = (samples || []).filter(s => s.action === 'would_rename');
      // dry-run leaves `renamed: 0` (nothing written); the live number is wouldRename.length
      const totalToRename = renamed || wouldRename.length;
      if (totalToRename === 0) {
        toast.success('No nameless suppliers found — your list is already clean.');
        return;
      }
      const previewLines = wouldRename.slice(0, 6).map(s => `• "${s.old_name || '(empty)'}" → "${s.new_name}"`).join('\n');
      const ok = window.confirm(
        `Found ${totalToRename} suppliers with empty/placeholder names.\n` +
        `Each will be renamed to its earliest expense's description.\n\n` +
        `Preview:\n${previewLines}\n\nRename these ${totalToRename} suppliers?`
      );
      if (!ok) return;
      const res = await api.post('/suppliers/backfill-names', {});
      const { renamed: applied = 0, skipped = 0 } = res.data || {};
      toast.success(`Renamed ${applied} suppliers. Skipped ${skipped}.`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Backfill failed');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (<DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>);
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* What is this page? — collapsible orientation help. */}
        <PageInfoCallout
          storageKey="suppliers"
          title="What is the Suppliers module?"
          tone="sky"
          onTour={() => setShowSuppliersTour(true)}
        >
          <p>
            Track every supplier and their <strong>credit balance</strong> in one place — from
            adding new vendors to logging credit purchases and reconciling payments. Use
            <strong> Pay from Bank</strong> on a supplier card to settle balances against a specific bank
            account (FIFO across unpaid invoices). The <strong>Cash flow movers</strong> panel below
            highlights suppliers whose payment volume changed sharply over the last 7 days — early
            warning on rate hikes, billing disputes, or relationship issues.
          </p>
        </PageInfoCallout>

        {/* iter403 — Inline IBAN suggestions just for suppliers. Same
            engine as the platform/customer variants — filters to
            outgoing bank txns whose description matches a supplier name. */}
        <PlatformIbanBanner days={90} entityTypes="supplier" testidPrefix="supp-iban" />

        {/* iter724 — Lost-payment audit banner. Fires when there are
            unmatched outgoing bank txns that are plausibly historical
            supplier payments which silently failed under the pre-fix
            bug. One-click jumps to Recon Cockpit where the user can
            bulk-re-pair them. Suppressed when zero — no UI noise. */}
        {lostPaymentsScan && lostPaymentsScan.total > 0 && (
          <Card
            className="border-amber-300 bg-gradient-to-br from-amber-50 to-orange-50/40"
            data-testid="lost-payments-banner"
          >
            <CardContent className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="rounded-full bg-amber-100 p-2 mt-0.5">
                  <AlertTriangle className="text-amber-700" size={18} />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-amber-900 text-sm">
                    {lostPaymentsScan.total} unmatched outgoing bank transaction{lostPaymentsScan.total === 1 ? '' : 's'} in the last 60 days — likely historical supplier payments that silently failed
                  </div>
                  <div className="text-xs text-amber-800 mt-0.5">
                    Worth <strong>{getCurrencySymbol()} {Math.round(lostPaymentsScan.total_amount).toLocaleString()}</strong> total
                    {lostPaymentsScan.likely_supplier > 0 && (
                      <> · <strong>{lostPaymentsScan.likely_supplier}</strong> match supplier-payment patterns ({getCurrencySymbol()} {Math.round(lostPaymentsScan.likely_amount).toLocaleString()})</>
                    )}.
                    Before iter724, Pay Credit → Bank Transfer + bank-line attempts could silently drop. Re-pair them in Recon Cockpit.
                  </div>
                </div>
              </div>
              <Link
                to="/bank-statements/reconcile?kind=debit"
                className="shrink-0"
                data-testid="lost-payments-cta"
              >
                <Button
                  size="sm"
                  className="bg-amber-600 hover:bg-amber-700 text-white rounded-xl whitespace-nowrap"
                >
                  Review in Recon Cockpit →
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {/* Cash Flow Movers — using the shared MoversPanel (same UX as
            Banking Top Movers and Sales Movers). The endpoint normalises
            into {id, name, current, previous, delta, direction}. */}
        <MoversPanel
          movers={cashflowMovers.map((m) => ({
            id: m.supplier_id,
            name: m.name,
            current: m.current_amount,
            previous: m.previous_amount,
            delta: m.delta,
            direction: m.direction,
          }))}
          title="Cash flow movers · last 7 days vs prior 7 days"
          subtitle="early warning on rate hikes &amp; disputes"
          tone="sky"
          unit="SAR paid"
          testidPrefix="suppliers"
          formatValue={(v) => Math.round(v).toLocaleString()}
        />

        {/* iter571 — Reconcile Worst-Offenders.
            Picks the 5 suppliers with the biggest |Outstanding − Current Credit|
            gap so the user can fix the largest data-integrity issues at
            month-end with one click each. */}
        {(() => {
          const offenders = (suppliers || [])
            .map((s) => {
              const ps = paySummaries[s.id] || { cash: 0, bank: 0, returns: 0 };
              const total = Number(s.total_purchases || 0);
              const cash = Number(ps.cash || 0);
              const bank = Number(ps.bank || 0);
              const returns = Number(ps.returns || 0);
              // iter578 — Outstanding must also subtract returns. Without
              // this, returns make Outstanding > current_credit by exactly
              // the returns total, producing phantom Δ gaps that aren't
              // real data inconsistencies.
              const outstanding = Math.max(0, total - cash - bank - returns);
              const credit = Number(s.current_credit || 0);
              const gap = Math.abs(outstanding - credit);
              return { s, total, cash, bank, outstanding, credit, gap };
            })
            .filter((x) => x.gap >= 1)
            .sort((a, b) => b.gap - a.gap)
            .slice(0, 5);
          if (offenders.length === 0) return null;
          const fmt0 = (n) => `${CUR} ${Math.round(Number(n) || 0).toLocaleString()}`;
          return (
            <div className="rounded-2xl border border-amber-300 bg-amber-50/40 p-3 sm:p-4" data-testid="worst-offenders-card">
              <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <AlertTriangle size={16} className="text-amber-700" />
                  <h3 className="font-semibold text-sm">Reconcile worst-offenders</h3>
                  <Badge variant="secondary" className="font-normal" data-testid="worst-offenders-count">{offenders.length}</Badge>
                </div>
                <span className="text-[10px] text-muted-foreground italic">Suppliers with the biggest mismatch between Outstanding and Current Credit. Click any row to fix.</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-2">
                {offenders.map((o, i) => (
                  <button
                    key={o.s.id}
                    type="button"
                    onClick={() => setReconcileSupplier(o.s)}
                    className="text-left p-2.5 rounded-xl border border-amber-200 bg-white hover:bg-amber-50/60 hover:border-amber-300 transition-colors"
                    data-testid={`worst-offenders-row-${i}`}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <div className="font-semibold text-xs truncate" title={o.s.name}>{o.s.name}</div>
                      <Badge variant="outline" className="text-[9px] border-rose-300 text-rose-700">Δ {fmt0(o.gap)}</Badge>
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground flex items-center justify-between gap-2">
                      <span>Outstanding {fmt0(o.outstanding)}</span>
                      <span>Credit {fmt0(o.credit)}</span>
                    </div>
                    <div className="mt-1.5 text-[10px] text-emerald-700 font-medium inline-flex items-center gap-1">
                      <Sparkles size={10} /> Click to reconcile →
                    </div>
                  </button>
                ))}
              </div>
            </div>
          );
        })()}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center shadow-lg shadow-blue-200/50">
              <Truck size={24} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="suppliers-page-title">Suppliers</h1>
              <p className="text-sm text-muted-foreground mt-0.5">Manage suppliers and track credit</p>
            </div>
          </div>
          <div className="flex gap-2 items-center flex-wrap">
            {/* Export ▾ — reuses existing ExportButtons handlers exactly (PDF/Excel/CSV) */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-xl h-9 text-xs" data-testid="suppliers-export-menu">
                  <Download size={14} className="mr-1" /> Export <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="p-2" data-testid="suppliers-export-menu-content">
                <DropdownMenuLabel className="text-xs">Export suppliers</DropdownMenuLabel>
                <div className="pt-1"><ExportButtons dataType="suppliers" /></div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* More Actions ▾ — occasional non-admin actions */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" className="rounded-xl h-9 text-xs" data-testid="suppliers-more-actions">
                  <MoreHorizontal size={14} className="mr-1" /> More <ChevronDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="suppliers-more-actions-content">
                <DropdownMenuItem asChild data-testid="suppliers-scan-invoice-item">
                  <Link to="/scan-invoice"><ScanLine size={14} className="mr-2 text-primary" /> Scan Invoice QR</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild data-testid="suppliers-returns-item">
                  <Link to="/bill-returns"><Undo2 size={14} className="mr-2" /> Returns</Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <div className="px-1 py-0.5" data-testid="suppliers-tour-wrap">
                  <PageTour storageKey="tour_suppliers" steps={[
                    { target: '[data-testid="add-supplier-button-2"]', title: 'Add Supplier', content: 'Create supplier profiles with contact info, category, credit limits, and bank details.' },
                    { target: '[data-testid="supplier-category-select-2"]', title: 'Categories', content: 'Organize suppliers by category and sub-category for easy filtering.' },
                    { target: '[data-testid="suppliers-admin-tools"]', title: 'Admin Tools', content: 'Maintenance actions (Fix Data, Cleanup, Backfill) live here for admins.' },
                  ]} />
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Admin Tools ▾ — maintenance ops, admin-only (backend also enforces) */}
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="rounded-xl h-9 text-xs text-warning border-warning/30 hover:bg-warning/10" data-testid="suppliers-admin-tools">
                    <ShieldCheck size={14} className="mr-1" /> Admin Tools <ChevronDown size={14} className="ml-1" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" data-testid="suppliers-admin-tools-content">
                  <DropdownMenuLabel className="text-xs">Maintenance</DropdownMenuLabel>
                  <DropdownMenuItem onSelect={previewMigration} data-testid="admin-fix-data-item">
                    <ArrowUpCircle size={14} className="mr-2 text-warning" /> Fix Data
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={cleanupAutoCreated} disabled={cleaningJunk} data-testid="admin-cleanup-item">
                    {cleaningJunk ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2 text-rose-600" />} Cleanup Bank Junk
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={backfillNames} disabled={backfilling} data-testid="admin-backfill-item">
                    {backfilling ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Sparkles size={14} className="mr-2 text-sky-700" />} Backfill Names
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button className="rounded-xl bg-primary hover:bg-primary/90" size="sm" data-testid="add-supplier-button-2">
                  <Plus size={14} className="mr-1" /> Add Supplier
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-lg max-h-[85dvh] overflow-y-auto" data-testid="supplier-dialog" aria-describedby="supplier-dialog-description">
                <DialogHeader>
                  <DialogTitle className="font-outfit">{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label>Supplier Name *</Label>
                    <Input value={formData.name} data-testid="supplier-name-input"
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })} required />
                  </div>
                  <div>
                    <Label>Category</Label>
                    <Select value={formData.category || "none"} onValueChange={(val) => setFormData({ ...formData, category: val === "none" ? "" : val })}>
                      <SelectTrigger data-testid="supplier-category-select-2"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No Category</SelectItem>
                        {categories.filter(c => !c.parent_id).map((cat) => (
                          <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="flex gap-2 mt-2">
                      <Input value={newCategory} onChange={(e) => setNewCategory(e.target.value)} placeholder="New category name" className="h-8 text-xs" data-testid="new-category-input" />
                      <Button type="button" size="sm" variant="outline" onClick={handleAddCategory} className="h-8 text-xs whitespace-nowrap" data-testid="add-category-button">
                        <Plus size={12} className="mr-1" /> Add
                      </Button>
                    </div>
                  </div>
                  {formData.category && (
                    <div>
                      <Label>Sub-Category</Label>
                      <Select value={formData.sub_category || "none"} onValueChange={(val) => setFormData({ ...formData, sub_category: val === "none" ? "" : val })}>
                        <SelectTrigger data-testid="supplier-subcategory-select"><SelectValue placeholder="Select sub-category" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Sub-Category</SelectItem>
                          {subCategories.map((cat) => <SelectItem key={cat.id} value={cat.name}>{cat.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2 mt-2">
                        <Input value={newSubCategory} onChange={(e) => setNewSubCategory(e.target.value)} placeholder="New sub-category" className="h-8 text-xs" data-testid="new-subcategory-input" />
                        <Button type="button" size="sm" variant="outline" onClick={handleAddSubCategory} className="h-8 text-xs whitespace-nowrap"><Plus size={12} className="mr-1" />Add</Button>
                      </div>
                    </div>
                  )}
                  <div>
                    <Label>Branch</Label>
                    <Select value={formData.branch_id || "all"} onValueChange={(val) => setFormData({ ...formData, branch_id: val === "all" ? "" : val })}>
                      <SelectTrigger data-testid="branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Branches</SelectItem>
                        {branches.map((branch) => (<SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Phone</Label>
                      <Input value={formData.phone} data-testid="supplier-phone-input" onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input type="email" value={formData.email} data-testid="supplier-email-input" onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Credit Limit</Label>
                      <Input type="number" step="0.01" value={formData.credit_limit} data-testid="credit-limit-input"
                        onChange={(e) => setFormData({ ...formData, credit_limit: parseFloat(e.target.value) || 0 })} />
                    </div>
                    <div>
                      <Label>VAT Number</Label>
                      <Input value={formData.vat_number || ''} inputMode="numeric" maxLength={15} placeholder="3XXXXXXXXXXXXX3" data-testid="supplier-vat-input"
                        onChange={(e) => setFormData({ ...formData, vat_number: e.target.value.replace(/\D/g, '') })} />
                      {formData.vat_number && !/^3\d{13}3$/.test(formData.vat_number) && <p className="text-[11px] text-amber-600 mt-1" data-testid="supplier-vat-hint">Saudi VAT numbers are 15 digits starting and ending with 3</p>}
                    </div>
                  </div>

                  {/* Bank Accounts Section */}
                  <div className="space-y-3 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <Label className="text-sm font-medium flex items-center gap-1.5">
                        <Building2 size={14} /> Bank Accounts
                        <span className="text-xs text-muted-foreground">({(formData.bank_accounts || []).length}/3)</span>
                      </Label>
                      {(formData.bank_accounts || []).length < 3 && (
                        <Button type="button" size="sm" variant="outline" onClick={addBankAccount}
                          className="h-7 text-xs" data-testid="add-bank-account-btn">
                          <Plus size={12} className="mr-1" /> Add Bank
                        </Button>
                      )}
                    </div>
                    {(formData.bank_accounts || []).map((ba, idx) => (
                      <div key={idx} className="p-3 bg-muted/50 rounded-lg border space-y-2" data-testid={`bank-account-${idx}`}>
                        <div className="flex items-center justify-between">
                          <span className="text-xs font-medium text-muted-foreground">Bank #{idx + 1}</span>
                          <button type="button" onClick={() => removeBankAccount(idx)} className="text-xs text-error hover:text-error">
                            <X size={14} />
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="Bank Name" value={ba.bank_name || ''} className="h-8 text-sm"
                            onChange={(e) => updateBankAccount(idx, 'bank_name', e.target.value)} data-testid={`bank-name-${idx}`} />
                          <Input placeholder="Account Number" value={ba.account_number || ''} className="h-8 text-sm"
                            onChange={(e) => updateBankAccount(idx, 'account_number', e.target.value)} data-testid={`bank-acc-${idx}`} />
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input placeholder="IBAN" value={ba.iban || ''} className="h-8 text-sm"
                            onChange={(e) => updateBankAccount(idx, 'iban', e.target.value)} data-testid={`bank-iban-${idx}`} />
                          <Input placeholder="SWIFT Code" value={ba.swift_code || ''} className="h-8 text-sm"
                            onChange={(e) => updateBankAccount(idx, 'swift_code', e.target.value)} data-testid={`bank-swift-${idx}`} />
                        </div>
                      </div>
                    ))}
                    {(formData.bank_accounts || []).length === 0 && (
                      <p className="text-xs text-muted-foreground text-center py-2">No bank accounts added</p>
                    )}
                  </div>

                  <div className="flex gap-3">
                    <Button type="submit" data-testid="submit-supplier-button" className="rounded-full">
                      {editingSupplier ? 'Update' : 'Add'} Supplier
                    </Button>
                    <Button type="button" variant="outline" onClick={() => setShowDialog(false)} className="rounded-full">Cancel</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Advanced Search */}
        <AdvancedSearch
          onSearch={setSearchFilters}
          config={{
            searchFields: ['name', 'phone', 'email', 'category'],
            placeholder: 'Search suppliers by name, category...',
            filters: [
              { key: 'branch_id', label: 'Branch', type: 'select', options: branches.map(b => ({ value: b.id, label: b.name })) },
              { key: 'category', label: 'Category', type: 'select', options: categories.filter(c => !c.parent_id).map(c => ({ value: c.name, label: c.name })) },
              { key: 'current_credit', label: 'Credit Balance', type: 'range' }
            ]
          }}
        />

        <div className="flex items-center justify-end gap-1 mb-1">
          <span className="text-[10px] text-muted-foreground mr-1 uppercase tracking-wide">View</span>
          <Button size="sm" variant={viewMode === 'grid' ? 'default' : 'outline'}
            onClick={() => setViewMode('grid')}
            className="h-7 px-2 rounded-l-md rounded-r-none"
            data-testid="suppliers-view-grid">
            <LayoutGrid size={12} className="mr-1" /> Grid
          </Button>
          <Button size="sm" variant={viewMode === 'list' ? 'default' : 'outline'}
            onClick={() => setViewMode('list')}
            className="h-7 px-2 rounded-l-none rounded-r-md"
            data-testid="suppliers-view-list">
            <List size={12} className="mr-1" /> List
          </Button>
        </div>

        {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {applySearchFilters(suppliers, searchFilters).map((supplier) => {
            const branchName = branches.find((b) => b.id === supplier.branch_id)?.name || 'All Branches';
            const creditUtilization = supplier.credit_limit > 0 ? (supplier.current_credit / supplier.credit_limit) * 100 : 0;
            const totalPurchases = supplier.total_purchases || 0;
            // iter589 — Per-card Quick Reconcile pill. Mirrors the iter578
            // formula so the per-card gap matches the worst-offenders
            // panel + the reconcile dialog math (zero phantom mismatches).
            const _ps = paySummaries[supplier.id] || { cash: 0, bank: 0, returns: 0 };
            const _outstanding = Math.max(0, totalPurchases - Number(_ps.cash || 0) - Number(_ps.bank || 0) - Number(_ps.returns || 0));
            const _credit = Math.max(0, Number(supplier.current_credit || 0));
            const _reconGap = Math.abs(_outstanding - _credit);

            return (
              <div key={supplier.id} className="bg-white dark:bg-muted rounded-2xl shadow-sm hover:shadow-lg transition-all p-4 space-y-3" data-testid="supplier-card">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <h3 className="font-semibold text-sm text-foreground dark:text-white truncate">{supplier.name}</h3>
                      {/* Reconciled/Unlinked — bank-link status. A supplier with
                          bank account details on file auto-reconciles against
                          uploaded bank statements; without them, bank payments
                          can't be matched to this vendor. */}
                      {(supplier.bank_accounts && supplier.bank_accounts.length > 0) ? (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-900"
                          title="Bank details on file — payments auto-reconcile against uploaded statements"
                          data-testid={`supplier-linked-badge-${supplier.id}`}
                        >
                          <Link2 size={9} /> Reconciled
                        </span>
                      ) : (
                        <span
                          className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200 dark:bg-stone-800/50 dark:text-stone-400 dark:border-stone-700"
                          title="No bank account on file — bank payments can't auto-match. Add bank details to enable reconciliation."
                          data-testid={`supplier-unlinked-badge-${supplier.id}`}
                        >
                          <Link2Off size={9} /> Unlinked
                        </span>
                      )}
                      {/* iter589 — Quick Reconcile pill. Only renders for
                          suppliers that have some activity (totalPurchases > 0)
                          to avoid noise on brand-new vendors. */}
                      {totalPurchases > 0 && (
                        _reconGap < 1 ? (
                          <button
                            type="button"
                            onClick={() => setReconcileSupplier(supplier)}
                            title="Outstanding matches Current Credit — click to open the reconcile snapshot anyway"
                            className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900 transition-colors"
                            data-testid={`quick-recon-pill-${supplier.id}`}
                          >
                            <Sparkles size={9} /> In sync
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={() => setReconcileSupplier(supplier)}
                            title={`Outstanding (${CUR} ${_outstanding.toFixed(2)}) ≠ Current Credit (${CUR} ${_credit.toFixed(2)}). Click to investigate.`}
                            className={`inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full border transition-colors ${
                              _reconGap >= 100
                                ? 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100 dark:bg-rose-950/30 dark:text-rose-300 dark:border-rose-900'
                                : 'bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900'
                            }`}
                            data-testid={`quick-recon-pill-${supplier.id}`}
                          >
                            Δ {_reconGap.toLocaleString(undefined, { maximumFractionDigits: 0 })} · Reconcile
                          </button>
                        )
                      )}
                    </div>
                    <div className="flex items-center gap-2 mt-1">
                      {supplier.category && (
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{supplier.category}</span>
                      )}
                      <span className="text-[10px] text-muted-foreground">
                        {supplier.branch_id ? <BranchLink branchId={supplier.branch_id} name={branchName} className="text-[10px]" /> : branchName}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-primary" onClick={() => handleEdit(supplier)} data-testid="edit-supplier-button"><Edit size={13} /></Button>
                    <Button size="icon" variant="ghost" className="h-7 w-7 rounded-lg text-muted-foreground hover:text-error" onClick={() => handleDelete(supplier.id)} data-testid="delete-supplier-button"><Trash2 size={13} /></Button>
                  </div>
                </div>
                {supplier.phone && <p className="text-xs text-muted-foreground">{supplier.phone}</p>}

                {/* Total Purchases */}
                <div className="p-2.5 bg-warning/10 dark:bg-amber-900/10 rounded-xl" data-testid={`total-purchases-${supplier.id}`}>
                  <div className="flex items-center justify-between">
                    <span className="text-[11px] font-medium text-warning flex items-center gap-1"><Receipt size={11} /> Total Purchases</span>
                    <span className="text-sm font-bold text-warning inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {totalPurchases.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                  </div>
                </div>

                {/* iter569 — Cash/Bank Paid Breakdown.
                    Always shown for visual consistency (was hidden when
                    both = 0, which made Arz Khairat-style cards look
                    incomplete). */}
                {(() => {
                  const ps = paySummaries[supplier.id] || { cash: 0, bank: 0, returns: 0 };
                  const cash = Number(ps.cash || 0);
                  const bank = Number(ps.bank || 0);
                  const returns = Number(ps.returns || 0);
                  const credit = Number(supplier.current_credit || 0);
                  // iter578 — Outstanding now subtracts returns (supplier
                  // credit-returns + bill-returns) to match the formula used
                  // for current_credit on the backend's /suppliers GET. Without
                  // this subtraction, returns produced phantom gaps between
                  // the Outstanding line and Credit Status (user-reported).
                  const outstanding = Math.max(0, totalPurchases - cash - bank - returns);
                  const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
                  return (
                    <div className="pt-2 border-t border-border dark:border-stone-700 space-y-2">
                      <div className="flex gap-2">
                        <div className="flex-1 p-2 bg-success/10 dark:bg-emerald-900/10 rounded-lg text-center">
                          <div className="text-[10px] text-muted-foreground">Cash Paid</div>
                          <div className="text-xs font-bold text-success inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {fmt(cash)}</div>
                        </div>
                        <div className="flex-1 p-2 bg-info/10 dark:bg-blue-900/10 rounded-lg text-center">
                          <div className="text-[10px] text-muted-foreground">Bank Paid</div>
                          <div className="text-xs font-bold text-info inline-flex items-center gap-0.5"><RiyalIcon size="0.85em" /> {fmt(bank)}</div>
                        </div>
                      </div>
                      {/* iter578 — Show returns total if any (so the
                          Outstanding = Total − Paid − Returns math is fully
                          visible to the user). */}
                      {returns > 0 && (
                        <div className="flex items-center justify-between text-[11px] px-2 py-0.5 text-muted-foreground" data-testid={`returns-${supplier.id}`} title="Sum of supplier credit-returns + bill-returns — these reduce the live current_credit balance">
                          <span>Returns</span>
                          <span className="inline-flex items-center gap-0.5 text-rose-700"><RiyalIcon size="0.75em" /> −{fmt(returns)}</span>
                        </div>
                      )}
                      {/* iter569 — Outstanding line. iter578 — formula now
                          includes returns so it matches Credit Status. */}
                      {(outstanding > 0 || credit > 0) && (
                        <div className={`flex items-center justify-between text-xs px-2 py-1 rounded-md ${outstanding > 0 ? 'bg-rose-50 dark:bg-rose-900/15 text-rose-700 dark:text-rose-300' : 'bg-emerald-50 dark:bg-emerald-900/15 text-emerald-700 dark:text-emerald-300'}`} data-testid={`outstanding-${supplier.id}`}>
                          <span className="font-medium">Outstanding {returns > 0 ? '(Total − Paid − Returns)' : '(Total − Paid)'}</span>
                          <span className="font-bold inline-flex items-center gap-0.5"><RiyalIcon size="0.8em" /> {fmt(outstanding)}</span>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Bank Accounts Preview */}
                {supplier.bank_accounts && supplier.bank_accounts.length > 0 && (
                  <div className="pt-2 border-t border-border dark:border-stone-700">
                    <p className="text-[10px] text-muted-foreground mb-1 flex items-center gap-1"><Building2 size={10} /> Bank Accounts ({supplier.bank_accounts.length})</p>
                    {supplier.bank_accounts.map((ba, i) => (
                        <div key={i} className="text-xs text-muted-foreground bg-muted/50 px-2 py-1 rounded mb-1">
                          {ba.bank_name || 'Bank'}: {ba.account_number || ba.iban || '-'}
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="pt-3 border-t border-border dark:border-stone-700">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-muted-foreground">Credit Status</span>
                      <span className="text-xs font-bold text-foreground dark:text-stone-200">{CUR} {supplier.current_credit?.toFixed(2) || '0.00'} / {supplier.credit_limit?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="w-full bg-muted dark:bg-stone-700 h-1.5 rounded-full overflow-hidden">
                      <div className={`h-full transition-all rounded-full ${creditUtilization > 80 ? 'bg-error' : creditUtilization > 50 ? 'bg-warning' : 'bg-success'}`}
                        style={{ width: `${Math.min(creditUtilization, 100)}%` }} />
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <Button size="sm" variant="outline" onClick={() => { setPayingSupplier(supplier); setShowAddBillDialog(true); setBillData({ amount: '', category: '', payment_mode: 'credit', description: '', branch_id: supplier.branch_id || '' }); }}
                      data-testid={`add-bill-${supplier.id}`} className="text-warning border-warning/30 hover:bg-warning/10 rounded-lg text-xs h-8">
                      <Receipt size={12} className="mr-1" /> Bill
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setPayingSupplier(supplier); setShowPayDialog(true); }}
                      data-testid="pay-credit-button"
                      className={`rounded-lg text-xs h-8 ${supplier.current_credit > 0 ? 'text-info border-info/30 hover:bg-info/10' : 'text-muted-foreground border-border'}`}
                      disabled={!supplier.current_credit || supplier.current_credit <= 0}>
                      <DollarSign size={12} className="mr-1" /> Pay Credit
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setBankPaySeed({ payee_type: 'supplier', payee_id: supplier.id, payee_label: supplier.name }); setShowBankPay(true); }}
                      data-testid={`pay-bank-${supplier.id}`}
                      className="text-emerald-700 border-emerald-300 hover:bg-emerald-50 dark:hover:bg-emerald-950/20 rounded-lg text-xs h-8"
                      title="Pay this supplier from a bank account"
                    >
                      <Banknote size={12} className="mr-1" /> Pay from Bank
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => { setLedgerStartDate(''); setLedgerEndDate(''); viewLedger(supplier); }}
                      data-testid={`view-ledger-${supplier.id}`} className="text-muted-foreground border-border hover:bg-muted/50 rounded-lg text-xs h-8">
                      <FileText size={12} className="mr-1" /> Ledger
                    </Button>
                  </div>
                  {/* iter570 — One-click Reconcile dialog */}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setReconcileSupplier(supplier)}
                    className="w-full mt-2 rounded-lg text-xs h-8 border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    data-testid={`reconcile-supplier-${supplier.id}`}
                    title="Inspect every bill + payment and attach unpaid bills to bank lines in one screen"
                  >
                    <Sparkles size={12} className="mr-1" /> Reconcile this supplier
                  </Button>
              </div>
            );
          })}
          {suppliers.length === 0 && (
            <div className="col-span-full text-center py-16 text-muted-foreground">
              <p className="text-sm">No suppliers yet. Add your first supplier to get started!</p>
            </div>
          )}
        </div>
        ) : (
          <div className="bg-white dark:bg-muted rounded-xl shadow-sm overflow-hidden" data-testid="suppliers-list-view">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-[11px] uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="text-left px-3 py-2">Supplier</th>
                    <th className="text-left px-3 py-2 hidden md:table-cell">Category</th>
                    <th className="text-left px-3 py-2 hidden lg:table-cell">Branch</th>
                    <th className="text-right px-3 py-2">Purchases</th>
                    <th className="text-right px-3 py-2 hidden sm:table-cell">Credit Owed</th>
                    <th className="text-right px-3 py-2 hidden lg:table-cell">Credit Limit</th>
                    <th className="text-center px-3 py-2 w-1">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applySearchFilters(suppliers, searchFilters).map((supplier) => {
                    const branchName = branches.find((b) => b.id === supplier.branch_id)?.name || 'All Branches';
                    return (
                      <tr key={supplier.id} className="border-t border-border hover:bg-muted/30 transition-colors"
                        data-testid={`supplier-row-${supplier.id}`}>
                        <td className="px-3 py-2 font-medium">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate max-w-[14rem]">{supplier.name}</span>
                            {(supplier.bank_accounts && supplier.bank_accounts.length > 0) ? (
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-sky-50 text-sky-700 border border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-900"
                                title="Bank details on file — payments auto-reconcile against uploaded statements"
                                data-testid={`supplier-linked-badge-${supplier.id}`}
                              >
                                <Link2 size={9} /> Reconciled
                              </span>
                            ) : (
                              <span
                                className="inline-flex items-center gap-0.5 text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-stone-100 text-stone-500 border border-stone-200 dark:bg-stone-800/50 dark:text-stone-400 dark:border-stone-700"
                                title="No bank account on file — bank payments can't auto-match. Add bank details to enable reconciliation."
                                data-testid={`supplier-unlinked-badge-${supplier.id}`}
                              >
                                <Link2Off size={9} /> Unlinked
                              </span>
                            )}
                          </div>
                          {supplier.phone && <div className="text-[10px] text-muted-foreground">{supplier.phone}</div>}
                        </td>
                        <td className="px-3 py-2 hidden md:table-cell text-xs text-muted-foreground">{supplier.category || '—'}</td>
                        <td className="px-3 py-2 hidden lg:table-cell text-xs text-muted-foreground">{branchName}</td>
                        <td className="px-3 py-2 text-right font-medium text-warning whitespace-nowrap">
                          <span className="inline-flex items-center gap-0.5"><RiyalIcon size="0.8em" /> {(supplier.total_purchases || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        </td>
                        <td className="px-3 py-2 text-right hidden sm:table-cell font-medium text-error whitespace-nowrap">
                          <span className="inline-flex items-center gap-0.5"><RiyalIcon size="0.8em" /> {(supplier.current_credit || 0).toFixed(2)}</span>
                        </td>
                        <td className="px-3 py-2 text-right hidden lg:table-cell text-xs text-muted-foreground whitespace-nowrap">
                          {supplier.credit_limit ? `${CUR} ${supplier.credit_limit.toFixed(0)}` : '—'}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex items-center gap-1 justify-end">
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-info"
                              onClick={() => { setPayingSupplier(supplier); setShowAddBillDialog(true); setBillData({ amount: '', category: '', payment_mode: 'credit', description: '', branch_id: supplier.branch_id || '' }); }}
                              data-testid={`list-add-bill-${supplier.id}`} title="Add bill">
                              <Receipt size={12} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-success"
                              disabled={!supplier.current_credit || supplier.current_credit <= 0}
                              onClick={() => { setPayingSupplier(supplier); setShowPayDialog(true); }}
                              data-testid={`list-pay-credit-${supplier.id}`} title="Pay credit">
                              <DollarSign size={12} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6"
                              onClick={() => { setLedgerStartDate(''); setLedgerEndDate(''); viewLedger(supplier); }}
                              data-testid={`list-view-ledger-${supplier.id}`} title="Ledger">
                              <FileText size={12} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6"
                              onClick={() => handleEdit(supplier)}
                              data-testid={`list-edit-${supplier.id}`} title="Edit">
                              <Edit size={12} />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-6 w-6 text-error"
                              onClick={() => handleDelete(supplier.id)}
                              data-testid={`list-delete-${supplier.id}`} title="Delete">
                              <Trash2 size={12} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {suppliers.length === 0 && (
                    <tr><td colSpan={7} className="text-center py-12 text-muted-foreground text-sm">No suppliers yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Pay Credit Dialog */}
        <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
          <DialogContent data-testid="pay-credit-dialog">
            <DialogHeader><DialogTitle className="font-outfit">Pay Supplier Credit</DialogTitle></DialogHeader>
            <form onSubmit={handlePayCredit} className="space-y-4">
              <div>
                <p className="text-sm text-muted-foreground mb-2">Paying to: <span className="font-medium text-foreground">{payingSupplier?.name}</span></p>
                <p className="text-sm text-muted-foreground">Current Credit: <span className="font-bold text-error">{CUR} {payingSupplier?.current_credit?.toFixed(2)}</span></p>
              </div>
              <div>
                <Label>Payment Amount *</Label>
                <Input type="number" step="0.01" value={paymentData.amount} data-testid="payment-amount-input"
                  onChange={(e) => setPaymentData({ ...paymentData, amount: e.target.value })} required max={payingSupplier?.current_credit} />
              </div>
              <div>
                <Label>Payment Mode *</Label>
                <Select value={paymentData.payment_mode} onValueChange={(val) => setPaymentData({ ...paymentData, payment_mode: val })}>
                  <SelectTrigger data-testid="payment-mode-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="bank">Bank</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>From Branch</Label>
                <Select value={paymentData.branch_id || "all"} onValueChange={(val) => setPaymentData({ ...paymentData, branch_id: val === "all" ? "" : val })}>
                  <SelectTrigger data-testid="pay-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">No Branch</SelectItem>
                    {branches.map((branch) => (<SelectItem key={branch.id} value={branch.id}>{branch.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-3">
                <Button type="submit" data-testid="submit-payment-button" className="rounded-full">Pay Credit</Button>
                <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)} className="rounded-full">Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Supplier Ledger Dialog */}
        <Dialog open={showLedgerDialog} onOpenChange={setShowLedgerDialog}>
          <DialogContent className="max-w-7xl w-[96vw] max-h-[92dvh] overflow-hidden flex flex-col" data-testid="ledger-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileText className="text-info" />
                {ledgerData?.supplier?.name || ledgerSupplier?.name} - Ledger
              </DialogTitle>
            </DialogHeader>

            {/* H2.1 — Period presets */}
            <div className="flex flex-wrap items-center gap-1.5 pb-2">
              {[
                { k: 'this', label: 'This Month' },
                { k: 'last', label: 'Last Month' },
                { k: 'all', label: 'All Time' },
              ].map(p => (
                <Button key={p.k} size="sm" variant="outline" className="h-7 text-xs" data-testid={`ledger-preset-${p.k}`}
                  onClick={() => {
                    const now = new Date();
                    let s = '', e = '';
                    if (p.k === 'this') {
                      s = new Date(now.getFullYear(), now.getMonth(), 1).toISOString().slice(0, 10);
                      e = new Date(now.getFullYear(), now.getMonth() + 1, 0).toISOString().slice(0, 10);
                    } else if (p.k === 'last') {
                      s = new Date(now.getFullYear(), now.getMonth() - 1, 1).toISOString().slice(0, 10);
                      e = new Date(now.getFullYear(), now.getMonth(), 0).toISOString().slice(0, 10);
                    }
                    setLedgerStartDate(s); setLedgerEndDate(e);
                    if (ledgerSupplier) setTimeout(() => viewLedger(ledgerSupplier, s, e, ledgerBranchId), 0);
                  }}>{p.label}</Button>
              ))}
              <span className="text-xs text-muted-foreground ml-1">Custom:</span>
            </div>

            {/* Date Filter */}
            <div className="flex flex-wrap items-end gap-2 pb-3 border-b">
              <div>
                <Label className="text-xs">From</Label>
                <Input type="date" value={ledgerStartDate} onChange={e => setLedgerStartDate(e.target.value)}
                  className="h-8 text-sm w-36" data-testid="ledger-start-date" />
              </div>
              <div>
                <Label className="text-xs">To</Label>
                <Input type="date" value={ledgerEndDate} onChange={e => setLedgerEndDate(e.target.value)}
                  className="h-8 text-sm w-36" data-testid="ledger-end-date" />
              </div>
              {/* iter526 — Branch filter on supplier ledger */}
              <div>
                <Label className="text-xs">Branch</Label>
                <Select value={ledgerBranchId} onValueChange={(v) => setLedgerBranchId(v)}>
                  <SelectTrigger className="h-8 text-sm w-44" data-testid="ledger-branch-filter"><SelectValue placeholder="All branches" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All branches</SelectItem>
                    {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {/* H2.1 — Opening balance toggle (account-statement vs period-only) */}
              <div>
                <Label className="text-xs">Opening Balance</Label>
                <Button size="sm" variant={ledgerOpening ? 'default' : 'outline'} className="h-8 text-xs w-full"
                  data-testid="ledger-opening-toggle"
                  onClick={() => {
                    const next = !ledgerOpening; setLedgerOpening(next);
                    if (ledgerSupplier) setTimeout(() => viewLedger(ledgerSupplier, ledgerStartDate, ledgerEndDate, ledgerBranchId), 0);
                  }}>{ledgerOpening ? 'ON' : 'OFF'}</Button>
              </div>
              <Button size="sm" variant="outline" className="h-8"
                onClick={() => ledgerSupplier && viewLedger(ledgerSupplier, ledgerStartDate, ledgerEndDate, ledgerBranchId)}
                data-testid="ledger-filter-btn">Filter</Button>
              <div className="ml-auto flex gap-1.5">
                <Button size="sm" variant="outline" className="h-8 text-info border-info/30 hover:bg-info/10"
                  onClick={openShareDialog} data-testid="share-statement-btn">
                  <Send size={13} className="mr-1" /> Share
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-error border-error/30 hover:bg-error/10"
                  onClick={() => exportLedger('pdf')} data-testid="export-ledger-pdf">
                  <Download size={13} className="mr-1" /> PDF
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-success border-success/30 hover:bg-success/10"
                  onClick={() => exportLedger('excel')} data-testid="export-ledger-excel">
                  <Download size={13} className="mr-1" /> Excel
                </Button>
                <Button size="sm" variant="outline" className="h-8 text-muted-foreground border-border hover:bg-muted/50"
                  onClick={() => exportLedger('csv')} data-testid="export-ledger-csv">
                  <Download size={13} className="mr-1" /> CSV
                </Button>
              </div>
            </div>

            {ledgerLoading ? (
              <div className="flex items-center justify-center py-8">Loading...</div>
            ) : ledgerData && (
              <div className="flex-1 overflow-auto space-y-4">
                {/* iter405 — Inline IBAN suggestions filtered to THIS
                    supplier only. Surfaces unmatched bank-statement
                    IBANs whose description references the supplier. */}
                <PlatformIbanBanner
                  days={90}
                  entityTypes="supplier"
                  entityId={ledgerSupplier?.id}
                  testidPrefix="ledger-supp-iban"
                />

                {/* Current Balance */}
                <div className="p-4 bg-primary/5 rounded-xl border border-primary/20">
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      {ledgerData.summary?.opening_included ? 'Closing Balance (Outstanding)' : 'Period Net Activity'}
                    </p>
                    <p className="text-3xl font-bold text-primary" data-testid="ledger-balance">
                      {CUR} {(ledgerData.summary?.opening_included ? ledgerData.summary?.closing_balance : ledgerData.summary?.period_net_activity)?.toFixed(2) || '0.00'}
                    </p>
                    {!ledgerData.summary?.opening_included && (ledgerData.period?.start !== 'All time') && (
                      <p className="text-[11px] text-muted-foreground mt-1">Period activity only — not the supplier's actual outstanding. Turn Opening Balance ON for the true balance.</p>
                    )}
                  </div>
                  {ledgerData.summary?.opening_included && (
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-3 text-center" data-testid="ledger-opening-breakdown">
                      <div><p className="text-[11px] text-muted-foreground">Opening</p><p className="text-sm font-semibold inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.opening_balance || 0).toLocaleString()}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">+ Period Purchases</p><p className="text-sm font-semibold text-warning inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.period_purchases || 0).toLocaleString()}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">− Payments/Returns</p><p className="text-sm font-semibold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {((ledgerData.summary?.period_payments || 0) + (ledgerData.summary?.period_returns || 0)).toLocaleString()}</p></div>
                      <div><p className="text-[11px] text-muted-foreground">= Closing</p><p className="text-sm font-bold text-primary inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.closing_balance || 0).toLocaleString()}</p></div>
                    </div>
                  )}
                </div>

                {/* Summary Cards — unified with design tokens */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2" data-testid="ledger-summary-cards">
                  <div className="p-3 bg-muted rounded-lg border border-border text-center" data-testid="ledger-total-purchases">
                    <p className="text-xs text-muted-foreground font-medium">Total Purchases</p>
                    <p className="text-lg font-bold inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.total_purchases || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">Credit + cash/bank</p>
                  </div>
                  <div className="p-3 bg-warning/10 rounded-lg border border-warning/30 text-center" data-testid="ledger-credit-purchases">
                    <p className="text-xs text-warning/90 font-medium">Credit Purchases</p>
                    <p className="text-lg font-bold text-warning inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.credit_purchases || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-warning/70">Adds to balance</p>
                  </div>
                  <div className="p-3 bg-success/10 rounded-lg border border-success/30 text-center" data-testid="ledger-paid-at-purchase">
                    <p className="text-xs text-success/90 font-medium">Paid at Purchase</p>
                    <p className="text-lg font-bold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.paid_at_purchase || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-success/70">Cash {(ledgerData.summary?.cash_purchases || 0).toLocaleString()} · Bank {(ledgerData.summary?.bank_purchases || 0).toLocaleString()}</p>
                  </div>
                  <div className="p-3 bg-info/10 rounded-lg border border-info/30 text-center" data-testid="ledger-credit-repayments">
                    <p className="text-xs text-info/90 font-medium">Credit Repayments</p>
                    <p className="text-lg font-bold text-info inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.total_payments || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-info/70">Reduces balance</p>
                  </div>
                  <div className="p-3 bg-success/10 rounded-lg border border-success/30 text-center" data-testid="ledger-total-paid">
                    <p className="text-xs text-success/90 font-medium">Total Paid</p>
                    <p className="text-lg font-bold text-success inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.total_paid || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-success/70">At purchase + repayments</p>
                  </div>
                  <div className="p-3 bg-muted rounded-lg border border-border text-center" data-testid="ledger-closing-balance">
                    <p className="text-xs text-muted-foreground font-medium">Closing Balance</p>
                    <p className="text-lg font-bold text-foreground inline-flex items-center gap-1"><RiyalIcon size="0.7em" /> {(ledgerData.summary?.closing_balance || 0).toLocaleString()}</p>
                    <p className="text-[10px] text-muted-foreground">{ledgerData.entry_count || 0} entries</p>
                  </div>
                </div>
                {(ledgerData.summary?.unverified_settlement_count || 0) > 0 && (
                  <div className="p-2 rounded-lg border border-warning/40 bg-warning/10 text-xs text-warning" data-testid="ledger-unverified-settlements">
                    {ledgerData.summary.unverified_settlement_count} repayment expense(s) totalling SAR {(ledgerData.summary.unverified_settlements || 0).toLocaleString()} have no matching supplier payment — not counted as purchases or payments until reviewed.
                  </div>
                )}

                {/* Filter chips */}
                <div className="flex gap-1.5 flex-wrap" data-testid="ledger-filter-chips">
                  {[
                    { key: 'all', label: 'All', count: ledgerData.entries?.length || 0 },
                    { key: 'purchases', label: 'Purchases', count: (ledgerData.entries || []).filter(e => e.type === 'purchase').length },
                    { key: 'payments', label: 'Payments', count: (ledgerData.entries || []).filter(e => e.type === 'payment' && !e.reversed_at).length },
                    { key: 'reversed', label: 'Reversed', count: (ledgerData.entries || []).filter(e => e.reversed_at).length },
                  ].map(c => (
                    <button
                      key={c.key}
                      type="button"
                      onClick={() => setLedgerFilter(c.key)}
                      className={`px-3 py-1 rounded-full text-xs font-medium border transition ${
                        ledgerFilter === c.key
                          ? c.key === 'reversed' ? 'bg-warning text-white border-warning'
                            : c.key === 'payments' ? 'bg-info text-white border-info'
                              : c.key === 'purchases' ? 'bg-success text-white border-success'
                                : 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-border hover:border-primary/40'
                      }`}
                      data-testid={`ledger-filter-${c.key}`}
                    >
                      {c.label} <span className="opacity-70">({c.count})</span>
                    </button>
                  ))}
                </div>

                {/* Ledger Entries Table */}
                <div className="border rounded-lg overflow-hidden">
                  {/* Desktop Grid */}
                  <div className="hidden md:block">
                  <div className="hidden md:grid grid-cols-[repeat(15,minmax(0,1fr))] gap-2 bg-muted px-3 py-2 text-xs font-semibold sticky top-0">
                    <span className="col-span-2">Date</span>
                    <span className="col-span-2">Type</span>
                    <span className="col-span-2">Branch</span>
                    <span className="col-span-3">Description</span>
                    <span className="col-span-2 text-right">Debit</span>
                    <span className="col-span-1 text-right">Credit</span>
                    <span className="col-span-2 text-right">Balance</span>
                    <span className="col-span-1 text-center">Act</span>
                  </div>
                  <div className="max-h-[300px] overflow-y-auto">
                    {(ledgerData.entries || []).filter(e => {
                      if (ledgerFilter === 'purchases') return e.type === 'purchase';
                      if (ledgerFilter === 'payments') return e.type === 'payment' && !e.reversed_at;
                      if (ledgerFilter === 'reversed') return !!e.reversed_at;
                      return true;
                    }).map((entry, idx) => (
                      <div key={idx} className={`px-3 py-2 text-xs grid grid-cols-[repeat(15,minmax(0,1fr))] gap-2 border-t ${
                        entry.reversed_at ? 'bg-muted text-muted-foreground'
                          : entry.type === 'purchase' ? 'bg-warning/5' : 'bg-info/5'
                      }`} data-testid={`ledger-entry-${idx}`}>
                        <span className={`col-span-2 text-muted-foreground ${entry.reversed_at ? 'line-through' : ''}`}>
                          {entry.date ? new Date(entry.date).toLocaleDateString() : '-'}
                        </span>
                        <span className="col-span-2">
                          <Badge variant="outline" className={`text-[10px] ${
                            entry.reversed_at ? 'bg-muted text-muted-foreground'
                              : entry.type === 'purchase'
                                ? entry.payment_mode === 'credit' ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success'
                                : 'bg-info/20 text-info'
                          }`}>
                            {entry.reversed_at ? 'Reversed'
                              : entry.type === 'purchase'
                                ? (entry.payment_mode === 'credit' ? 'Credit Purchase' : 'Paid Purchase')
                                : 'Payment'}
                          </Badge>
                        </span>
                        <span className="col-span-2">
                          <Badge variant="outline" className="text-[10px] bg-muted text-muted-foreground">{entry.branch_name || '-'}</Badge>
                          {/* iter721 — cross-branch badge: paying-branch ≠ benefiting-branch */}
                          {entry.expense_for_branch_id && entry.expense_for_branch_id !== entry.branch_id && (
                            <Badge
                              variant="outline"
                              className="text-[9px] bg-info/10 text-info border-info/30 ml-1 whitespace-nowrap"
                              title={`Paid by ${entry.branch_name || '-'} for ${entry.expense_for_branch_name || '-'}`}
                              data-testid={`ledger-xbranch-badge-${idx}`}
                            >
                              ↔ {entry.expense_for_branch_name || 'Cross-branch'}
                            </Badge>
                          )}
                        </span>
                        <span className="col-span-3 truncate" title={entry.description}>
                          <span className={entry.reversed_at ? 'line-through' : ''}>{entry.description || '-'}</span>
                          {entry.reversed_at && (
                            <div className="text-[9px] text-muted-foreground no-underline mt-0.5">
                              Reversed {new Date(entry.reversed_at).toLocaleDateString()}{entry.reversed_by_name ? ` by ${entry.reversed_by_name}` : ''}
                              {entry.reverse_reason && ` · ${entry.reverse_reason}`}
                            </div>
                          )}
                        </span>
                        <span className={`col-span-2 text-right font-medium ${entry.debit > 0 ? 'text-warning' : ''}`}>
                          {entry.debit > 0 ? `${entry.debit.toFixed(2)}` : ''}
                        </span>
                        <span className={`col-span-1 text-right font-medium ${entry.credit > 0 ? 'text-info' : entry.reversed_at ? 'line-through' : ''}`}>
                          {entry.reversed_at
                            ? (entry.original_amount ? entry.original_amount.toFixed(2) : '')
                            : entry.credit > 0 ? `${entry.credit.toFixed(2)}` : ''}
                        </span>
                        <span className={`col-span-2 text-right font-bold ${
                          entry.reversed_at ? 'text-muted-foreground'
                            : entry.type === 'purchase' ? 'text-warning' : 'text-info'
                        }`}>{entry.balance?.toFixed(2)}</span>
                        <span className="col-span-1 text-center">
                          {entry.type === 'payment' && entry.payment_id && !entry.reversed_at && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-6 w-6 p-0 text-muted-foreground hover:text-warning hover:bg-warning/10"
                              onClick={() => reversePayment(entry)}
                              title="Undo payment (audit trail kept)"
                              data-testid={`ledger-undo-${idx}`}
                            >
                              <Undo2 size={12} />
                            </Button>
                          )}
                        </span>
                      </div>
                    ))}
                    {(!ledgerData.entries || ledgerData.entries.length === 0) && (
                      <div className="px-3 py-8 text-center text-muted-foreground">No transactions found</div>
                    )}
                  </div>
                </div>
                  {/* Mobile Ledger Cards */}
                  <div className="md:hidden max-h-[350px] overflow-y-auto divide-y divide-border">
                    {ledgerData.entries?.map((entry, idx) => (
                      <div key={idx} className={`p-3 ${entry.type === 'purchase' ? 'bg-warning/5' : 'bg-info/5'}`} data-testid={`ledger-mobile-entry-${idx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <Badge variant="outline" className={`text-[10px] ${entry.type === 'purchase' ? entry.payment_mode === 'credit' ? 'bg-warning/20 text-warning' : 'bg-success/20 text-success' : 'bg-info/20 text-info'}`}>
                              {entry.type === 'purchase' ? (entry.payment_mode === 'credit' ? 'Credit' : 'Cash') : 'Payment'}
                            </Badge>
                            <span className="text-[10px] text-muted-foreground">{entry.date ? new Date(entry.date).toLocaleDateString() : '-'}</span>
                            {entry.expense_for_branch_id && entry.expense_for_branch_id !== entry.branch_id && (
                              <Badge
                                variant="outline"
                                className="text-[9px] bg-info/10 text-info border-info/30"
                                title={`Paid by ${entry.branch_name || '-'} for ${entry.expense_for_branch_name || '-'}`}
                                data-testid={`ledger-mobile-xbranch-badge-${idx}`}
                              >
                                ↔ {entry.expense_for_branch_name || 'X-branch'}
                              </Badge>
                            )}
                          </div>
                          <span className="font-bold text-xs">Bal: {entry.balance?.toFixed(2)}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] text-muted-foreground truncate max-w-[50%]">{entry.description || entry.branch_name || '-'}</span>
                          <div className="flex gap-2 text-[10px]">
                            {entry.debit > 0 && <span className="text-warning font-semibold">Dr: {entry.debit.toFixed(2)}</span>}
                            {entry.credit > 0 && <span className="text-info font-semibold">Cr: {entry.credit.toFixed(2)}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                    {(!ledgerData.entries || ledgerData.entries.length === 0) && (
                      <div className="px-3 py-8 text-center text-muted-foreground">No transactions found</div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Purchase Bill Dialog */}
        <Dialog open={showAddBillDialog} onOpenChange={setShowAddBillDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Receipt className="text-warning" /> Add Purchase Bill</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddBill} className="space-y-4">
              {/* Supplier info (pre-selected, changeable) */}
              <div>
                <Label>Supplier</Label>
                <Select value={payingSupplier?.id || "none"} onValueChange={(v) => {
                  const s = suppliers.find(sup => sup.id === v);
                  if (s) { setPayingSupplier(s); setBillData(prev => ({ ...prev, branch_id: s.branch_id || prev.branch_id })); }
                }}>
                  <SelectTrigger data-testid="bill-supplier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {suppliers.map(s => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              {/* Branch Selection */}
              <div>
                <Label>Branch</Label>
                <Select value={billData.branch_id || "all"} onValueChange={(v) => setBillData({ ...billData, branch_id: v === "all" ? "" : v })}>
                  <SelectTrigger data-testid="bill-branch-select"><SelectValue placeholder="Select Branch" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Branches</SelectItem>
                    {branches.map(b => (<SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              <div className="p-3 bg-warning/10 rounded-lg border border-warning/30 text-sm">
                <p className="font-medium text-warning">What is a Purchase Bill?</p>
                <p className="text-warning text-xs mt-1">
                  A purchase bill is when you BUY goods from this supplier.
                  Choose <strong>Credit</strong> if you'll pay later (adds to balance),
                  or <strong>Cash/Bank</strong> if paid now.
                </p>
              </div>
              <div>
                <Label>Amount (SR)</Label>
                <Input type="number" value={billData.amount} onChange={(e) => setBillData({ ...billData, amount: e.target.value })} placeholder="0.00" required className="text-lg font-bold" data-testid="bill-amount-input" />
              </div>
              <div>
                <Label>Payment Type</Label>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-1">
                  {['credit', 'cash', 'bank'].map(mode => (
                    <button key={mode} type="button" onClick={() => setBillData({ ...billData, payment_mode: mode })}
                      className={`py-2.5 px-3 rounded-lg text-sm font-medium transition-all border ${
                        billData.payment_mode === mode
                          ? mode === 'credit' ? 'bg-warning text-white border-warning'
                            : mode === 'cash' ? 'bg-success text-white border-success'
                            : 'bg-info text-white border-info'
                          : 'bg-white text-muted-foreground border-border hover:bg-muted/50'
                      }`} data-testid={`bill-mode-${mode}`}>
                      {mode === 'credit' && <Receipt size={14} className="inline mr-1" />}
                      {mode === 'cash' && <Banknote size={14} className="inline mr-1" />}
                      {mode === 'bank' && <CreditCard size={14} className="inline mr-1" />}
                      {mode.charAt(0).toUpperCase() + mode.slice(1)}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {billData.payment_mode === 'credit' ? 'Credit: Will add to supplier balance (pay later)' : 'Paid: No balance change'}
                </p>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={billData.category || "general"} onValueChange={(v) => setBillData({ ...billData, category: v })}>
                  <SelectTrigger><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Supplier Purchase">Supplier Purchase</SelectItem>
                    <SelectItem value="Inventory">Inventory</SelectItem>
                    <SelectItem value="Raw Materials">Raw Materials</SelectItem>
                    <SelectItem value="Supplies">Supplies</SelectItem>
                    {expenseCategories.map(c => (<SelectItem key={c.id || c.name} value={c.name}>{c.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description (Optional)</Label>
                <Input value={billData.description} onChange={(e) => setBillData({ ...billData, description: e.target.value })} placeholder="e.g., Invoice #1234" data-testid="bill-description-input" />
              </div>
              <VatTreatmentFields value={billData} amount={billData.amount} onChange={(p) => setBillData((b) => ({ ...b, ...p }))} testPrefix="supplier-bill-vat" />
              <div className="flex gap-3 pt-2">
                <Button type="submit" className="flex-1 bg-warning hover:bg-warning" data-testid="submit-bill-btn"><Receipt size={16} className="mr-1" /> Add Purchase Bill</Button>
                <Button type="button" variant="outline" onClick={() => setShowAddBillDialog(false)}>Cancel</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Share Statement Dialog */}
        <Dialog open={showShareDialog} onOpenChange={setShowShareDialog}>
          <DialogContent className="max-w-sm" data-testid="share-statement-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Send className="text-info" size={18} /> Share Statement</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Send <strong>{ledgerSupplier?.name}</strong>'s statement via:
              </p>

              {/* Channel Selection */}
              <div className="flex gap-3">
                <button type="button" onClick={() => toggleShareChannel('email')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all text-center ${
                    shareData.channels.includes('email')
                      ? 'border-info bg-info/10 text-info'
                      : 'border-border text-muted-foreground hover:border-border'
                  }`} data-testid="share-email-toggle">
                  <Mail size={20} className="mx-auto mb-1" />
                  <span className="text-sm font-medium">Email</span>
                  <p className="text-[10px] mt-0.5">PDF attached</p>
                </button>
                <button type="button" onClick={() => toggleShareChannel('whatsapp')}
                  className={`flex-1 p-3 rounded-lg border-2 transition-all text-center ${
                    shareData.channels.includes('whatsapp')
                      ? 'border-success bg-success/10 text-success'
                      : 'border-border text-muted-foreground hover:border-border'
                  }`} data-testid="share-whatsapp-toggle">
                  <MessageSquare size={20} className="mx-auto mb-1" />
                  <span className="text-sm font-medium">WhatsApp</span>
                  <p className="text-[10px] mt-0.5">Summary text</p>
                </button>
              </div>

              {/* Email Input */}
              {shareData.channels.includes('email') && (
                <div>
                  <Label className="text-xs">Email Address</Label>
                  <Input value={shareData.email} onChange={e => setShareData({ ...shareData, email: e.target.value })}
                    placeholder="supplier@example.com" type="email" data-testid="share-email-input" />
                </div>
              )}

              {/* Phone Input */}
              {shareData.channels.includes('whatsapp') && (
                <div>
                  <Label className="text-xs">WhatsApp Number (with country code)</Label>
                  <Input value={shareData.phone} onChange={e => setShareData({ ...shareData, phone: e.target.value })}
                    placeholder="+966512345678" data-testid="share-phone-input" />
                </div>
              )}

              <Button onClick={shareStatement} disabled={sharing || shareData.channels.length === 0}
                className="w-full" data-testid="send-statement-btn">
                {sharing ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Send size={16} className="mr-2" />}
                {sharing ? 'Sending...' : 'Send Statement'}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Migration Dialog */}
        <Dialog open={showMigrateDialog} onOpenChange={setShowMigrateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-warning"><ArrowUpCircle /> Fix Payment Data - Convert to Purchase Bills</DialogTitle>
            </DialogHeader>
            {migrationPreview && (
              <div className="space-y-4">
                <div className="p-3 bg-warning/10 rounded-lg border border-warning/30 text-sm">
                  <p className="font-medium text-warning">What will happen?</p>
                  <p className="text-warning mt-1">Your supplier payments will be converted to <strong>Purchase Bills (Expenses)</strong> with credit payment mode.</p>
                </div>
                <div className="p-4 bg-muted/50 rounded-lg border">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-warning">{migrationPreview.total_payments}</p>
                      <p className="text-xs text-muted-foreground">Payments to Convert</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-success">{CUR} {migrationPreview.total_amount?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">Total Amount</p>
                    </div>
                  </div>
                </div>
                {migrationPreview.total_payments > 0 ? (
                  <>
                    <div className="max-h-48 overflow-y-auto border rounded-lg">
                      <div className="p-2 bg-muted text-xs font-medium sticky top-0">By Supplier</div>
                      {Object.entries(migrationPreview.by_supplier || {}).map(([name, data]) => (
                        <div key={name} className="px-3 py-2 border-t flex justify-between text-sm">
                          <span>{name}</span>
                          <span className="text-muted-foreground">{data.count} entries - {CUR} {data.total?.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-3">
                      <Button onClick={executeMigration} disabled={migrating} className="flex-1 bg-warning hover:bg-warning">
                        {migrating ? 'Converting...' : 'Convert to Purchase Bills'}
                      </Button>
                      <Button variant="outline" onClick={() => setShowMigrateDialog(false)}>Cancel</Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-4 text-muted-foreground">
                    <p>No payments to migrate!</p>
                    <p className="text-xs mt-1">All data is already correct.</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Pay from Bank — reuses the global unified dialog,
            seeded with this supplier so the user doesn't have to
            re-pick the payee on /bank-payments. */}
        <BankPaymentDialog
          open={showBankPay}
          onOpenChange={setShowBankPay}
          seedPayee={bankPaySeed}
          onCreated={() => { setShowBankPay(false); setBankPaySeed(null); fetchData(); toast.success('Bank payment recorded'); }}
        />
        {/* iter570 — one-click Reconcile dialog */}
        <SupplierReconcileDialog
          supplier={reconcileSupplier}
          paySummary={reconcileSupplier ? paySummaries[reconcileSupplier.id] : null}
          onClose={() => setReconcileSupplier(null)}
          onOpenBankPay={(s) => {
            setReconcileSupplier(null);
            setBankPaySeed({ payee_type: 'supplier', payee_id: s.id, payee_label: s.name });
            setShowBankPay(true);
          }}
          onOpenPayCredit={(s) => {
            setReconcileSupplier(null);
            setPayingSupplier(s);
            setShowPayDialog(true);
          }}
        />
        {/* 3-step Suppliers walkthrough — opened from the info callout. */}
        <SuppliersTour open={showSuppliersTour} onOpenChange={setShowSuppliersTour} />
      </div>
    </DashboardLayout>
  );
}
